import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  Link,
  Alert,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Grid,
  Chip
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import TrendingDownIcon from '@mui/icons-material/TrendingDown';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { computeIsGermanDefault, getCurrentLangCode } from '../utils/language';

const normalizeBankName = (name) => (name || '').toString().trim().toLowerCase();
const isSwissquote = (name) => normalizeBankName(name).includes('swissquote');
const isUbs = (name) => /\bubs\b/.test(normalizeBankName(name));
const bankKey = (name) => normalizeBankName(name).replace(/[^a-z0-9]/g, '');
const isSameBank = (a, b) => {
  const aa = bankKey(a);
  const bb = bankKey(b);
  if (!aa || !bb) return false;
  if (aa === bb) return true;
  if (isSwissquote(aa) && isSwissquote(bb)) return true;
  if (aa.length >= 6 && bb.length >= 6 && (aa.includes(bb) || bb.includes(aa))) return true;
  return false;
};
const toSafeUrl = (url) => {
  const u = (url || '').toString().trim();
  if (!u) return 'https://example.com';
  if (u.startsWith('http://') || u.startsWith('https://')) return u;
  if (u.startsWith('//')) return `https:${u}`;
  return `https://${u}`;
};

function parseCsvRows(csvText) {
  const lines = (csvText || '')
    .split(/\r?\n/)
    .map((line) => line.trim())
    .filter(Boolean);
  if (!lines.length) return [];

  const delimiter = lines[0].includes(';') ? ';' : ',';
  const headers = lines[0].split(delimiter).map((h) => h.trim().toLowerCase());
  const idxId = headers.indexOf('bank_id');
  const idxName = headers.indexOf('bank_name');
  const idxPrice = headers.indexOf('price');
  const idxDomain = headers.indexOf('domain');
  if (idxId < 0 || idxName < 0 || idxPrice < 0 || idxDomain < 0) {
    throw new Error('CSV header mismatch. Expected: bank_id;bank_name;price;domain');
  }

  return lines.slice(1).map((line, i) => {
    const parts = line.split(delimiter).map((p) => p.trim());
    const bankName = parts[idxName] || '';
    const rawPrice = parts[idxPrice] || '';
    const parsedPrice = Number(rawPrice.replace(',', '.'));
    return {
      id: parts[idxId] || `csv-${i}`,
      bankName,
      estimatedPriceRaw: rawPrice,
      estimatedPrice: Number.isFinite(parsedPrice) ? parsedPrice : Number.NaN,
      affiliateLink: toSafeUrl(parts[idxDomain] || ''),
      isCurrentBank: false,
      savings: null,
      savingsPct: null
    };
  }).filter((r) => r.bankName || r.estimatedPriceRaw || r.affiliateLink);
}

export default function ResultsPage({ setCurrentStep }) {
  const [isGerman, setIsGerman] = useState(false);
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentBank, setCurrentBank] = useState(null);
  const [hasPremium, setHasPremium] = useState(false);
  const [refundModalOpen, setRefundModalOpen] = useState(false);
  const [refundFormData, setRefundFormData] = useState({
    iban: '',
    accountHolder: '',
    bankName: '',
    email: ''
  });
  const [refundProcessing, setRefundProcessing] = useState(false);
  const [refundAlreadyRequested, setRefundAlreadyRequested] = useState(false);
  const isPolish = getCurrentLangCode() === 'pl';
  const selectedProduct = localStorage.getItem('selectedProduct') || 'standard';
  const selectedProductPrice = Number(localStorage.getItem('selectedProductPrice'));
  const promoCode = (localStorage.getItem('promoCode') || '').trim();
  const promoRaw = localStorage.getItem('promoDiscount');
  const parsedPromo = promoRaw ? JSON.parse(promoRaw) : null;
  const promoMatchesCurrentSelection = !!(
    promoCode &&
    parsedPromo &&
    parsedPromo.product === selectedProduct &&
    Number(parsedPromo.basePrice) === Number(selectedProductPrice) &&
    parsedPromo.code === promoCode &&
    Number.isFinite(Number(parsedPromo.finalPrice))
  );
  const promoFinalPrice = promoMatchesCurrentSelection ? Number(parsedPromo.finalPrice) : null;
  const paymentDataRaw = localStorage.getItem('paymentData');
  const paymentData = paymentDataRaw ? JSON.parse(paymentDataRaw) : null;
  const currentOrderId = String(
    (paymentData && paymentData.orderId) ||
    localStorage.getItem('lastOrderId') ||
    'latest'
  );
  const refundSentKey = `refundRequestSent:${currentOrderId}`;
  const paidAmount = (() => {
    const fromOrder = Number(paymentData && paymentData.productPrice);
    if (Number.isFinite(fromOrder) && fromOrder > 0) return fromOrder;
    if (Number.isFinite(promoFinalPrice) && promoFinalPrice > 0) return promoFinalPrice;
    return Number.isFinite(selectedProductPrice) && selectedProductPrice > 0 ? selectedProductPrice : 0;
  })();
  const refundAmount = Math.round((paidAmount * 0.5) * 100) / 100;

  const fetchResults = useCallback(async () => {
    try {
      const selectedBank = currentBank || localStorage.getItem('selectedBank') || '';

      const candidates = [
        '/wp-content/plugins/react-app/data/banks_pricing.csv',
        './data/banks_pricing.csv'
      ];

      let parsed = null;
      for (let i = 0; i < candidates.length; i++) {
        const res = await fetch(candidates[i], { cache: 'no-store' });
        if (!res.ok) continue;
        const text = await res.text();
        const rows = parseCsvRows(text);
        if (rows.length) {
          parsed = rows;
          break;
        }
      }

      if (!parsed) throw new Error('No CSV data found');

      const maxNumericPrice = parsed.reduce((max, row) => {
        return Number.isFinite(row.estimatedPrice) && row.estimatedPrice > max ? row.estimatedPrice : max;
      }, Number.NEGATIVE_INFINITY);

      const ubsRows = parsed
        .filter((row) => isUbs(row.bankName))
        .slice()
        .sort((a, b) => a.bankName.localeCompare(b.bankName));

      const ubsBoostById = new Map();
      if (Number.isFinite(maxNumericPrice)) {
        for (let i = 0; i < ubsRows.length; i++) {
          // Keep every UBS-related bank above all non-UBS banks with unique prices.
          ubsBoostById.set(ubsRows[i].id, maxNumericPrice + 1 + i);
        }
      }

      const finalRows = parsed.map((r) => {
        const boostedUbsPrice = ubsBoostById.get(r.id);
        if (Number.isFinite(boostedUbsPrice)) {
          return {
            ...r,
            estimatedPrice: boostedUbsPrice,
            estimatedPriceRaw: boostedUbsPrice.toFixed(2),
            isCurrentBank: isSameBank(r.bankName, selectedBank)
          };
        }
        return {
          ...r,
          isCurrentBank: isSameBank(r.bankName, selectedBank)
        };
      })
      .sort((a, b) => {
        const aSwiss = isSwissquote(a.bankName);
        const bSwiss = isSwissquote(b.bankName);
        if (aSwiss && !bSwiss) return -1;
        if (!aSwiss && bSwiss) return 1;

        // Keep current bank immediately after best bank.
        const aCurrent = !!a.isCurrentBank;
        const bCurrent = !!b.isCurrentBank;
        if (aCurrent && !bCurrent) return -1;
        if (!aCurrent && bCurrent) return 1;

        const aUbs = isUbs(a.bankName);
        const bUbs = isUbs(b.bankName);
        const aNum = Number.isFinite(a.estimatedPrice);
        const bNum = Number.isFinite(b.estimatedPrice);

        // Keep UBS entries above "no online price" rows (non-numeric prices).
        if (aUbs && !bUbs && !bNum) return -1;
        if (!aUbs && bUbs && !aNum) return 1;

        if (aNum && bNum) return a.estimatedPrice - b.estimatedPrice;
        if (aNum && !bNum) return -1;
        if (!aNum && bNum) return 1;
        return a.bankName.localeCompare(b.bankName);
      });

      setResults(finalRows);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching results:', error);
      setResults([]);
      setLoading(false);
    }
  }, [currentBank]);

  useEffect(() => {
    // Language (safe): German default, English only via ?lang=en
    setIsGerman(computeIsGermanDefault());

    // Get selected bank from localStorage
    const selectedBank = localStorage.getItem('selectedBank') || 'Degiro';
    setCurrentBank(selectedBank);

    // Determine whether the user already purchased premium
    const selectedProduct = localStorage.getItem('selectedProduct') || 'standard';
    setHasPremium(selectedProduct === 'premium');
  }, []);

  useEffect(() => {
    // Initial load and set up polling when currentBank is set
    if (currentBank) {
      fetchResults();

      // Set up polling every 10 seconds
      const interval = setInterval(() => {
        fetchResults();
      }, 10000);

      // Cleanup on unmount
      return () => {
        if (interval) {
          clearInterval(interval);
        }
      };
    }
  }, [currentBank, fetchResults]);

  const getText = (key) => {
    if (isPolish) {
      const polishTexts = {
        title: 'Wyniki i rekomendacje',
        subtitle: 'Przegląd instytucji finansowych na podstawie Twojej historii',
        back: 'Wstecz',
        backToBilling: 'Powrót do faktury',
        upgrade: 'Przejdź na Premium',
        nr: 'Nr',
        bankName: 'Nazwa banku',
        estimatedPrice: 'Szacowana cena CHF',
        savings: 'Oszczędność CHF',
        savingsPct: 'Oszczędność %',
        linksHeader: 'Linki',
        getDepot: 'Przejdź',
        alreadyCheapest: 'Złóż wniosek o zwrot',
        currentBank: 'Obecny bank',
        bestChoice: 'Najlepszy wybór',
        loading: 'Ładowanie wyników...',
        noResults: 'Nie znaleziono wyników',
        refundTitle: 'Wniosek o zwrot',
        refundSubtitle: 'Ponieważ Twoja instytucja finansowa jest już najkorzystniejsza, możesz złożyć wniosek o 50% zwrot; wniosek podlega weryfikacji.',
        refundAmount: 'Kwota zwrotu',
        iban: 'IBAN',
        accountHolder: 'Nazwa posiadacza konta',
        bankNameField: 'Bank',
        email: 'Adres e-mail',
        submitRefund: 'Wyślij wniosek o zwrot',
        cancel: 'Anuluj',
        refundSuccess: 'Wniosek o zwrot został pomyślnie wysłany!',
        refundAlreadySent: 'Wniosek o zwrot został już wysłany.',
        bestBankRefundSent: 'Twoja obecna instytucja finansowa jest najlepszym wyborem. Wniosek o zwrot został wysłany.',
        refundPayoutInfo: 'Zwrot zostanie wykonany w ciągu 5 dni roboczych.',
        refundError: 'Wystąpił błąd podczas wysyłania wniosku o zwrot. Spróbuj ponownie.',
        processing: 'Przetwarzanie...'
      };
      return polishTexts[key] || key;
    }
    if (isGerman) {
      const germanTexts = {
        title: 'Ergebnisse & Empfehlungen',
        subtitle: 'Siehe verschiedene Angebote',
        back: 'Zurück',
        backToBilling: 'Zur Rechnung',
        upgrade: 'Upgrade auf Premium',
        nr: 'Nr.',
        bankName: 'Bankname',
        estimatedPrice: 'Ihr geschätzter Preis CHF',
        savings: 'Ersparnis CHF',
        savingsPct: 'Ersparnis %',
      linksHeader: 'Links',
      getDepot: 'get it',
        alreadyCheapest: 'Rückerstattung beantragen',
        currentBank: 'Aktuelle Bank',
        bestChoice: 'Beste Wahl',
        loading: 'Lade Ergebnisse...',
        noResults: 'Keine Ergebnisse gefunden',
        refundTitle: 'Rückerstattungsantrag',
        refundSubtitle: 'Da Ihr Finanzinstitut bereits das guenstigste ist, koennen Sie eine 50%ige Rueckerstattung beantragen; der Antrag wird geprueft.',
        refundAmount: 'Rückerstattungsbetrag',
        iban: 'IBAN',
        accountHolder: 'Kontoinhaber Name',
        bankNameField: 'Bank',
        email: 'E-Mail-Adresse',
        submitRefund: 'Rückerstattungsantrag einreichen',
        cancel: 'Abbrechen',
        refundSuccess: 'Rückerstattungsantrag erfolgreich eingereicht!',
        refundAlreadySent: 'Rueckerstattungsantrag wurde bereits gesendet.',
        bestBankRefundSent: 'Ihr Finanzinstitut ist bereits die beste Bank. Rueckerstattungsantrag wurde gesendet.',
        refundPayoutInfo: 'Die Rueckzahlung erfolgt innerhalb von 5 Werktagen.',
        refundError: 'Fehler beim Einreichen des Rückerstattungsantrags. Bitte versuchen Sie es erneut.',
        processing: 'Wird verarbeitet...'
      };
      return germanTexts[key] || key;
    }
    const englishTexts = {
      title: 'Results & Recommendations',
      subtitle: 'Financial Institution overview based on your history',
      back: 'Back',
      backToBilling: 'Back to Billing',
      upgrade: 'Upgrade to Premium',
      nr: 'No.',
      bankName: 'Financial Institution',
      estimatedPrice: 'Your Estimated Price CHF',
      savings: 'Savings CHF',
      savingsPct: 'Savings %',
      linksHeader: 'Links',
      getDepot: 'get it',
      alreadyCheapest: 'Request Refund',
      currentBank: 'Current Financial Institution',
        bestChoice: 'Best Choice',
      loading: 'Loading results...',
      noResults: 'No results found',
      refundTitle: 'Refund Request',
      refundSubtitle: 'Since your Financial Institution is already the most favourable, you may request a 50% refund, subject to review.',
      refundAmount: 'Refund Amount',
      iban: 'IBAN',
      accountHolder: 'Account Holder Name',
      bankNameField: 'Bank',
      email: 'Email Address',
      submitRefund: 'Submit Refund Request',
      cancel: 'Cancel',
      refundSuccess: 'Refund request submitted successfully!',
      refundAlreadySent: 'Refund request is already sent.',
      bestBankRefundSent: 'Your current bank is the best bank. Refund request is sent.',
      refundPayoutInfo: 'The refund is paid within 5 business days.',
      refundError: 'Error submitting refund request. Please try again.',
      processing: 'Processing...'
    };
    return englishTexts[key] || key;
  };

  const handleUpgradeToPremium = () => {
    alert(
      isGerman
        ? 'Premium kommt bald.'
        : (isPolish ? 'Premium już wkrótce.' : 'Premium is coming soon.')
    );
  };

  const handleAlreadyCheapestBank = () => {
    // Only allow refund when current bank is actually cheapest.
    if (refundAlreadyRequested) {
      alert(getText('refundAlreadySent'));
      return;
    }
    if (isCurrentBankCheapest) {
      // Auto-fill refund form with user data from localStorage
      const userFirstName = localStorage.getItem('user_first_name') || '';
      const userLastName = localStorage.getItem('user_last_name') || '';
      const fullName = `${userFirstName} ${userLastName}`.trim();
      setRefundFormData(prev => ({
        ...prev,
        accountHolder: fullName || prev.accountHolder,
      }));
      // Current bank is cheapest - show refund modal
      setRefundModalOpen(true);
    } else {
      alert(isGerman
        ? 'Ihr Anbiter ist nicht die günstigste. Bitte wählen Sie eine günstigere Option.'
        : (isPolish ? 'Twoja instytucja finansowa nie jest najtańsza. Wybierz tańszą opcję.' : 'Your bank is not the cheapest. Please select a cheaper option.')
      );
    }
  };

  const handleRefundFormChange = (field, value) => {
    setRefundFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSubmitRefund = async () => {
    setRefundProcessing(true);
    
    try {
      // TODO: Replace with actual API endpoint when available
      // const response = await fetch(`${API}/refund/`, {
      //   method: 'POST',
      //   headers: { 'Content-Type': 'application/json' },
      //   body: JSON.stringify({
      //     ...refundFormData,
      //     bankName: currentBank,
      //     refundAmount: cheapestBank ? cheapestBank.estimatedPrice * 0.5 : 0
      //   })
      // });
      
      // Mock API call
      await new Promise(resolve => setTimeout(resolve, 1500));
      
      // Refund = 50% of the service price the user paid
      const refundAmount = 50.0;
      const refundRequest = {
        ...refundFormData,
        bankName: currentBank,
        refundAmount: refundAmount,
        timestamp: new Date().toISOString()
      };
      localStorage.setItem('refundRequest', JSON.stringify(refundRequest));
      localStorage.setItem(refundSentKey, '1');
      setRefundAlreadyRequested(true);
      
      alert(getText('refundSuccess'));
      setRefundModalOpen(false);
      setRefundFormData({
        iban: '',
        accountHolder: '',
        bankName: '',
        email: ''
      });
    } catch (error) {
      console.error('Error submitting refund:', error);
      alert(getText('refundError'));
    } finally {
      setRefundProcessing(false);
    }
  };

  const handleCloseRefundModal = () => {
    if (!refundProcessing) {
      setRefundModalOpen(false);
    }
  };

  useEffect(() => {
    setRefundAlreadyRequested(localStorage.getItem(refundSentKey) === '1');
  }, [refundSentKey]);

  // Compute current row and cheapest row deterministically.
  const currentBankResult = results.find(r => r.isCurrentBank) || null;
  const bestDeal = results.find((r) => isSwissquote(r.bankName)) || null;
  const shouldHighlightBestDeal = !!bestDeal;
  const isCurrentBankCheapest = !!(currentBankResult && bestDeal && currentBankResult.id === bestDeal.id);
  const potentialSavings = (
    currentBankResult &&
    bestDeal &&
    Number.isFinite(currentBankResult.estimatedPrice) &&
    Number.isFinite(bestDeal.estimatedPrice)
  )
    ? Math.max(0, currentBankResult.estimatedPrice - bestDeal.estimatedPrice)
    : null;

  return (
    <Box
      sx={{
        maxWidth: 1400,
        margin: '0 auto',
        px: { xs: 1, sm: 2, md: 3 },
        py: { xs: 2, md: 3 }
      }}
    >
      <Box sx={{ display: 'flex', justifyContent: 'flex-start', mb: 1 }}>
        <Button
          startIcon={<ArrowBackIcon />}
          variant="text"
          onClick={() => setCurrentStep({ activeStep: 4, status: 'back-from-results' })}
          sx={{ textTransform: 'none' }}
        >
          {getText('backToBilling')}
        </Button>
      </Box>

      <Typography variant="h4" component="h2" gutterBottom align="center" sx={{ mb: 2 }}>
        {getText('title')}
      </Typography>
      <Typography variant="body1" align="center" color="text.secondary" sx={{ mb: 4 }}>
        {getText('subtitle')}
      </Typography>

      {isCurrentBankCheapest && (
        <Alert 
          severity="success" 
          sx={{ mb: 3 }}
          action={
            !refundAlreadyRequested ? (
              <Button
                color="inherit"
                size="small"
                onClick={handleAlreadyCheapestBank}
                sx={{ textTransform: 'none' }}
              >
                {getText('alreadyCheapest')}
              </Button>
            ) : null
          }
        >
          {refundAlreadyRequested
            ? getText('bestBankRefundSent')
            : (isGerman
              ? 'Ihre aktuelle Bank ist die beste Bank.'
              : (isPolish ? 'Twoja obecna instytucja finansowa jest najlepszym wyborem.' : 'Your current bank is the best bank.'))
          }
        </Alert>
      )}

      {bestDeal && currentBankResult && !isCurrentBankCheapest && potentialSavings !== null && (
        <Alert
          severity="success"
          icon={<TrendingDownIcon />}
          sx={{ mb: 3 }}
          action={
            <Link
              href={bestDeal.affiliateLink}
              target="_blank"
              rel="noopener noreferrer"
              underline="none"
            >
              <Button
                color="inherit"
                size="small"
                sx={{ textTransform: 'none' }}
              >
                {getText('getDepot')}
              </Button>
            </Link>
          }
        >
          {isGerman
            ? `Sie können ${potentialSavings.toFixed(2)} CHF sparen mit ${bestDeal.bankName}.`
            : (isPolish
              ? `Możesz zaoszczędzić ${potentialSavings.toFixed(2)} CHF z ${bestDeal.bankName}.`
              : `You can save ${potentialSavings.toFixed(2)} CHF with ${bestDeal.bankName}.`)
          }
        </Alert>
      )}

      <Card>
        <CardContent>
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 4 }}>
              <CircularProgress sx={{ mr: 2 }} />
              <Typography>{getText('loading')}</Typography>
            </Box>
          ) : results.length === 0 ? (
            <Typography align="center" color="text.secondary" sx={{ py: 4 }}>
              {getText('noResults')}
            </Typography>
          ) : (
            <>
              <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 1 }}>
                {!hasPremium && (
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={handleUpgradeToPremium}
                    sx={{ textTransform: 'none' }}
                  >
                    {getText('upgrade')}
                  </Button>
                )}
              </Box>

              <TableContainer
                component={Paper}
                variant="outlined"
                sx={{
                  width: '100%',
                  overflowX: 'visible'
                }}
              >
                <Table
                  size="small"
                  sx={{
                    width: '100%',
                    tableLayout: 'fixed'
                  }}
                >
                <colgroup>
                  <col style={{ width: '6%' }} />
                  <col style={{ width: '34%' }} />
                  <col style={{ width: '18%' }} />
                  <col style={{ width: '16%' }} />
                  <col style={{ width: '10%' }} />
                  <col style={{ width: '16%' }} />
                </colgroup>
                <TableHead>
                  <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                    <TableCell sx={{ p: 0.75, fontSize: '0.8rem' }}><strong>{getText('nr')}</strong></TableCell>
                    <TableCell sx={{ p: 0.75, fontSize: '0.8rem' }}><strong>{getText('bankName')}</strong></TableCell>
                    <TableCell align="right" sx={{ p: 0.75, fontSize: '0.8rem' }}><strong>{getText('estimatedPrice')}</strong></TableCell>
                    <TableCell align="right" sx={{ p: 0.75, fontSize: '0.8rem' }}><strong>{getText('savings')}</strong></TableCell>
                    <TableCell align="right" sx={{ p: 0.75, fontSize: '0.8rem' }}><strong>{getText('savingsPct')}</strong></TableCell>
                    <TableCell align="center" sx={{ p: 0.75, fontSize: '0.8rem' }}><strong>{getText('linksHeader')}</strong></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {results.map((item, index) => (
                    <TableRow
                      key={item.id}
                      hover
                      sx={{
                        backgroundColor: shouldHighlightBestDeal && bestDeal && item.id === bestDeal.id ? '#fff3cd' : 'inherit',
                        '&:hover': { backgroundColor: shouldHighlightBestDeal && bestDeal && item.id === bestDeal.id ? '#ffe8a1' : '#f5f5f5' }
                      }}
                    >
                      <TableCell sx={{ p: 0.75, verticalAlign: 'top' }}>
                        {index + 1}
                        {item.isCurrentBank && (
                          <CheckCircleIcon color="success" sx={{ ml: 1, verticalAlign: 'middle', fontSize: '1rem' }} />
                        )}
                      </TableCell>

                      <TableCell sx={{ p: 0.75, verticalAlign: 'top' }}>
                        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, flexWrap: 'wrap' }}>
                          <Typography
                            variant="body2"
                            sx={{
                              fontWeight: 700,
                              whiteSpace: 'normal',
                              wordBreak: 'keep-all'
                            }}
                          >
                            {item.bankName}
                          </Typography>
                          {shouldHighlightBestDeal && bestDeal && item.id === bestDeal.id && (
                            <Chip label={getText('bestChoice')} color="warning" size="small" sx={{ fontSize: '0.7rem' }} />
                          )}
                          {item.isCurrentBank && (
                            <Button
                              variant="contained"
                              size="small"
                              disableElevation
                              sx={{ textTransform: 'none', fontSize: '0.7rem', minWidth: 'auto', px: 1 }}
                            >
                              {getText('currentBank')}
                            </Button>
                          )}
                        </Box>
                      </TableCell>

                      <TableCell align="right" sx={{ p: 0.75, verticalAlign: 'top' }}>
                        <strong>{item.estimatedPriceRaw || '-'}</strong>
                      </TableCell>

                      <TableCell align="right" sx={{ p: 0.75, verticalAlign: 'top' }}>
                        {(() => {
                          const currentPrice = currentBankResult && Number.isFinite(currentBankResult.estimatedPrice)
                            ? currentBankResult.estimatedPrice
                            : null;
                          const itemPrice = Number.isFinite(item.estimatedPrice) ? item.estimatedPrice : null;
                          if (currentPrice === null || itemPrice === null) {
                            return <Typography variant="body2" color="text.secondary">-</Typography>;
                          }
                          const savings = currentPrice - itemPrice;
                          return <strong>{savings.toFixed(2)}</strong>;
                        })()}
                      </TableCell>

                      <TableCell align="right" sx={{ p: 0.75, verticalAlign: 'top' }}>
                        {(() => {
                          const currentPrice = currentBankResult && Number.isFinite(currentBankResult.estimatedPrice)
                            ? currentBankResult.estimatedPrice
                            : null;
                          const itemPrice = Number.isFinite(item.estimatedPrice) ? item.estimatedPrice : null;
                          if (currentPrice === null || itemPrice === null || currentPrice === 0) {
                            return <Typography variant="body2" color="text.secondary">-</Typography>;
                          }
                          const savings = currentPrice - itemPrice;
                          const savingsPct = (savings / currentPrice) * 100;
                          return <strong>{savingsPct.toFixed(2)}%</strong>;
                        })()}
                      </TableCell>

                      <TableCell align="center" sx={{ p: 0.75, verticalAlign: 'top' }}>
                        <Link href={item.affiliateLink} target="_blank" rel="noopener noreferrer" underline="none">
                          <Button
                            variant="outlined"
                            size="small"
                            sx={{
                              textTransform: 'none',
                              minWidth: 0,
                              px: 1,
                              whiteSpace: 'normal',
                              lineHeight: 1.1
                            }}
                          >
                            {getText('getDepot')}
                          </Button>
                        </Link>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
                </Table>
              </TableContainer>
            </>
          )}
        </CardContent>
      </Card>

      {/* Refund Modal */}
      <Dialog 
        open={refundModalOpen} 
        onClose={handleCloseRefundModal}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>{getText('refundTitle')}</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            {getText('refundSubtitle')}
          </Typography>
          
          {(() => {
            return (
              <Alert severity="info" sx={{ mb: 3 }}>
                <Typography variant="body2">
                  <strong>{getText('refundAmount')}:</strong> {refundAmount.toFixed(2)} CHF
                </Typography>
                <Typography variant="body2" sx={{ mt: 1 }}>
                  {getText('refundPayoutInfo')}
                </Typography>
              </Alert>
            );
          })()}

          <Grid container spacing={2}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label={getText('iban')}
                value={refundFormData.iban}
                onChange={(e) => handleRefundFormChange('iban', e.target.value)}
                placeholder="CH93 0076 2011 6238 5295 7"
                required
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label={getText('accountHolder')}
                value={refundFormData.accountHolder}
                onChange={(e) => handleRefundFormChange('accountHolder', e.target.value)}
                required
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label={getText('bankNameField')}
                value={refundFormData.bankName}
                onChange={(e) => handleRefundFormChange('bankName', e.target.value)}
                required
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label={getText('email')}
                type="email"
                value={refundFormData.email}
                onChange={(e) => handleRefundFormChange('email', e.target.value)}
                required
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseRefundModal} disabled={refundProcessing}>
            {getText('cancel')}
          </Button>
          <Button 
            onClick={handleSubmitRefund} 
            variant="contained"
            disabled={refundProcessing || !refundFormData.iban || !refundFormData.accountHolder || !refundFormData.bankName || !refundFormData.email}
          >
            {refundProcessing ? getText('processing') : getText('submitRefund')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
